import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, EmptyState, Pill } from "@/components/ui-bits";
import { Clock, FileQuestion, Play, Repeat, Ban } from "lucide-react";
import api from "@/lib/api";

export default function UpcomingTests() {
  const [items, setItems] = useState([]);
  const [done, setDone] = useState([]);

  useEffect(() => {
    api.get("/assessments").then(r => setItems(r.data));
    api.get("/results").then(r => setDone(r.data));
  }, []);

  const attemptsBy = done.reduce((acc, r) => {
    acc[r.assessment_id] = (acc[r.assessment_id] || 0) + 1;
    return acc;
  }, {});

  // "Upcoming" = assessments the student is allowed to attempt at least once more.
  const upcoming = items.filter(a => {
    const attempts = attemptsBy[a.id] || 0;
    if (a.allow_reattempts === false && attempts >= 1) return false;
    if (a.max_attempts && attempts >= Number(a.max_attempts)) return false;
    return true;
  });

  return (
    <div>
      <PageHeader title="Upcoming Tests" subtitle="Assessments available for you to take." />
      {upcoming.length === 0 ? <EmptyState title="Nothing pending" hint="You're all caught up." /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {upcoming.map(a => {
            const attempts = attemptsBy[a.id] || 0;
            const allow = a.allow_reattempts !== false;
            const maxAtt = a.max_attempts;
            const policyLabel = !allow
              ? "Single attempt"
              : maxAtt
                ? `${attempts}/${maxAtt} attempts used`
                : attempts > 0 ? `${attempts} previous · unlimited` : "Unlimited";
            return (
              <div key={a.id} data-testid={`upcoming-${a.id}`} className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md hover:border-slate-300 transition-all">
                <div className="text-lg font-semibold text-slate-900">{a.title}</div>
                <div className="mt-3 flex flex-wrap gap-2 text-sm text-slate-600">
                  <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-slate-400" /> {a.duration} min</span>
                  <span className="inline-flex items-center gap-1.5"><FileQuestion className="w-4 h-4 text-slate-400" /> {a.question_ids?.length || 0} questions</span>
                  <Pill tone="blue">Pass ≥ {a.passing_marks}%</Pill>
                  <Pill tone={allow ? "green" : "amber"}>
                    <Repeat className="inline-block w-3 h-3 mr-1 -mt-0.5" />
                    {policyLabel}
                  </Pill>
                </div>
                <div className="mt-5">
                  <Link data-testid={`start-${a.id}`} to={`/student/exam/${a.id}/intro`}
                    className="inline-flex items-center gap-2 bg-blue-600 text-white rounded-lg px-4 py-2 font-medium hover:bg-blue-700">
                    <Play className="w-4 h-4" /> {attempts > 0 ? "Re-attempt" : "Start assessment"}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
